import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { t } from "i18next";
import { ChangeEvent, ReactNode, useMemo, useState } from "react";
import { FaColumns, FaSearch, FaUndo } from "react-icons/fa";
import { SortDirection } from "../../autogen/bff-api";
import { HorizontalScrollBox } from "../HorizontalScrollBox";
import { ListLoadError } from "../ListLoadError";
import { SelectorValue } from "../input/Selector/SelectorValue";
import { Paging } from "../Paging";
import { ColumnHeader } from "./ColumnHeader";

export interface EntityTableColumn<T = Record<string, unknown>> {
  key: string;
  label: string;
  width?: string;
  hidden?: boolean;
  isFilterActive?: boolean;
  onSearch?: (value: string) => void;
  currentSearchValue?: string;
  onSort?: (sort: SortDirection) => void;
  currentSortDirection?: SortDirection;
  multiSelectConfig?: {
    options: SelectorValue<T>[];
    onSelect: (values: SelectorValue<T>[]) => void | Promise<void>;
    onInputChange?: (input: string) => void | Promise<void>;
    selectedValues?: SelectorValue<T>[];
    optionsCount?: number;
    optionsLimit?: number;
    isLoading?: boolean;
    selectedValuesLoading?: boolean;
  };
  singleSelectConfig?: {
    options: SelectorValue<T>[];
    onSelect: (value: SelectorValue<T>) => void | Promise<void>;
    onInputChange?: (input: string) => void | Promise<void>;
    selectedValue?: SelectorValue<T>;
    optionsCount?: number;
    optionsLimit?: number;
    isLoading?: boolean;
  };
  dateRangeConfig?: {
    onFromDateChange: (date?: string) => void | Promise<void>;
    onToDateChange: (date?: string) => void | Promise<void>;
    fromDate?: string | null;
    toDate?: string | null;
    fromDateLabel?: string;
    toDateLabel?: string;
  };
}

export const EntityTable = ({
  columns,
  rows,
  size = "md",
  debounceMs = 300,
  isLoading,
  count,
  limit,
  skip,
  setSkip,
  onReset,
  hasActiveFilters,
  resetKey,
  onDropdownOpen,
  onDropdownClose,
  onShowColumn,
  onHideColumn,
  hasColumnChanges,
  emptyText,
  noMatchText,
  loadError,
}: {
  columns: EntityTableColumn[];
  rows: ReactNode[][];
  size?: "sm" | "md" | "lg";
  debounceMs?: number;
  isLoading: boolean;
  count?: number;
  limit: number;
  skip: number;
  setSkip: (skip: number) => void;
  onReset?: () => void;
  hasActiveFilters?: boolean;
  resetKey?: string | number;
  onDropdownOpen?: (dropdownKey: string) => void;
  onDropdownClose?: (dropdownKey: string) => void;
  onShowColumn?: (columnKey: string) => void;
  onHideColumn?: (columnKey: string) => void;
  hasColumnChanges?: boolean;
  emptyText: string;
  noMatchText: string;
  loadError?: { message: string; onRetry: () => void };
}) => {
  const [columnSearchTerm, setColumnSearchTerm] = useState("");

  const toggleColumnVisibility = (columnKey: string) => {
    const shouldHide = !columns.find((col) => col.key === columnKey)?.hidden;
    if (shouldHide) onHideColumn?.(columnKey);
    else onShowColumn?.(columnKey);
  };

  const handleReset = () => {
    onReset?.();
  };

  const { visibleColumns, visibleColumnIndices } = useMemo(() => {
    const visibleColumns = columns.filter((column) => !column.hidden);
    const indices = columns.map((column, index) => (column.hidden ? -1 : index)).filter((index) => index !== -1);

    return { visibleColumns, visibleColumnIndices: indices };
  }, [columns]);

  const filteredColumns = useMemo(() => {
    if (!columnSearchTerm) return columns;
    const term = columnSearchTerm.toLowerCase();
    return columns.filter((c) => c.label.toLowerCase().includes(term) || c.key.toLowerCase().includes(term));
  }, [columns, columnSearchTerm]);

  const handleColumnSearchTermChange = (e: ChangeEvent<HTMLInputElement>) => {
    setColumnSearchTerm(e.target.value);
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const key = e.currentTarget.value;
    if (key) toggleColumnVisibility(key);
  };

  return (
    // Padding, not just the overflow guard: a focus ring is drawn outside the border box, and
    // setting one overflow axis makes CSS compute the other to `auto`, so this box clips the
    // toolbar buttons' rings on all four sides.
    <Flex direction="column" width="full" overflowX="hidden" p="1">
      <Flex justifyContent="space-between" pb="2">
        <Menu>
          <MenuButton
            as={Button}
            leftIcon={<Icon as={FaColumns} />}
            variant="outline"
            colorScheme="gray"
            color="smMuted"
            size="sm"
          >
            {t("Columns")}
          </MenuButton>
          <MenuList maxHeight="calc(100vh - 240px)" overflowY="auto">
            <Box px={3} py={2}>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="smMuted">
                {t("Show/Hide Columns")}
              </Text>
              <InputGroup size="sm" mb={2}>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaSearch} color="smMuted" />
                </InputLeftElement>
                <Input
                  value={columnSearchTerm}
                  onChange={handleColumnSearchTermChange}
                  placeholder={`${t("Search columns")}`}
                />
              </InputGroup>
              {filteredColumns.map((column) => (
                <Box key={column.key} px={2} py={1}>
                  <Checkbox value={column.key} isChecked={!column.hidden} onChange={handleCheckboxChange} size="sm">
                    <Text fontSize="sm">{column.label}</Text>
                  </Checkbox>
                </Box>
              ))}
            </Box>
          </MenuList>
        </Menu>
        {onReset && (
          <Button
            leftIcon={<Icon as={FaUndo} />}
            variant="outline"
            colorScheme="gray"
            color="smMuted"
            size="sm"
            onClick={handleReset}
            isDisabled={!hasActiveFilters && !hasColumnChanges}
          >
            {t("Reset table")}
          </Button>
        )}
      </Flex>
      {count !== undefined && (
        <Paging isLoading={isLoading} count={count} limit={limit} skip={skip} setSkip={setSkip} />
      )}
      <HorizontalScrollBox border="1px solid" borderColor="smBorder" rounded="md">
        <Table size={size}>
          <Thead>
            <Tr>
              {visibleColumns.map((column) => (
                <ColumnHeader
                  key={`${column.key}-${resetKey || "default"}`}
                  column={column}
                  debounceMs={debounceMs}
                  onDropdownOpen={onDropdownOpen}
                  onDropdownClose={onDropdownClose}
                />
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && rows.length === 0
              ? Array.from({ length: Math.min(limit, 5) }, (_, rowIndex) => (
                  <Tr key={rowIndex}>
                    {visibleColumnIndices.map((columnIndex, cellIndex) => (
                      <Td key={cellIndex} width={columns[columnIndex].width}>
                        <Skeleton h="5" w="full" rounded="md" />
                      </Td>
                    ))}
                  </Tr>
                ))
              : rows.map((row, rowIndex) => (
                  <Tr key={rowIndex}>
                    {visibleColumnIndices.map((columnIndex, cellIndex) => (
                      <Td
                        key={cellIndex}
                        width={columns[columnIndex].width}
                        minWidth={columns[columnIndex].width}
                        maxWidth={columns[columnIndex].width}
                      >
                        {row[columnIndex]}
                      </Td>
                    ))}
                  </Tr>
                ))}
          </Tbody>
        </Table>
      </HorizontalScrollBox>
      {!isLoading && rows.length === 0 && loadError && (
        <ListLoadError
          message={loadError.message}
          onRetry={loadError.onRetry}
          reset={hasActiveFilters && onReset ? { label: t("Reset table"), onReset: handleReset } : undefined}
        />
      )}
      {!isLoading && rows.length === 0 && !loadError && (
        <Flex direction="column" align="center" gap="3" py="10" textAlign="center">
          <Text color="smMuted">{hasActiveFilters ? noMatchText : emptyText}</Text>
          {hasActiveFilters && onReset && (
            <Button
              leftIcon={<Icon as={FaUndo} />}
              variant="outline"
              colorScheme="gray"
              color="smMuted"
              size="sm"
              onClick={handleReset}
            >
              {t("Reset table")}
            </Button>
          )}
        </Flex>
      )}
      {count !== undefined && count > 0 ? (
        <Paging isLoading={isLoading} count={count} limit={limit} skip={skip} setSkip={setSkip} />
      ) : null}
    </Flex>
  );
};
